import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Turnstile } from '@marsidev/react-turnstile';
import { Download, ImagePlus, Loader2, Share2, ShieldCheck, Sparkles } from 'lucide-react';
import type { ArtGeneratorBlock } from '@/types/blocks';
import { supabase } from '@/lib/supabase';
import { composePublicArt, type PublicArtTemplate } from '@/features/art-generator/lib/artComposition';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 8 * 1024 * 1024;

function artFilename(title: string): string {
  return `${title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'arte'}-compartilhar.png`;
}

async function callPublicArtFunction(siteId: string, templateId: string, photo: File, turnstileToken: string): Promise<Blob> {
  const body = new FormData();
  body.set('site_id', siteId);
  body.set('template_id', templateId);
  body.set('turnstile_token', turnstileToken);
  body.set('photo', photo);
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-art-generate`, {
    method: 'POST',
    headers,
    body,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || 'Não foi possível preparar sua foto. Tente novamente.');
  }
  return response.blob();
}

export function PublicArtGenerator({ block, siteId }: { block: ArtGeneratorBlock; siteId?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['public-art-templates', siteId, block.templateIds],
    queryFn: async () => {
      if (!siteId) return [] as PublicArtTemplate[];
      let query = supabase.from('public_art_templates').select('*').eq('site_id', siteId).eq('published', true).order('updated_at', { ascending: false });
      if (block.templateIds.length) query = query.in('id', block.templateIds);
      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      return data;
    },
    enabled: Boolean(siteId),
  });

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? templates[0] ?? null,
    [selectedTemplateId, templates],
  );

  useEffect(() => {
    if (selectedTemplate && selectedTemplate.id !== selectedTemplateId) setSelectedTemplateId(selectedTemplate.id);
  }, [selectedTemplate, selectedTemplateId]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const choosePhoto = (file: File | undefined) => {
    setError('');
    setPreviewUrl((url) => { if (url) URL.revokeObjectURL(url); return null; });
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) return setError('Envie uma foto em JPEG, PNG ou WebP.');
    if (file.size > MAX_FILE_SIZE) return setError('A foto deve ter no máximo 8 MB.');
    setPhoto(file);
  };

  const generate = async () => {
    if (!siteId || !selectedTemplate || !photo) return;
    if (!consent) return setError('Confirme o aviso de privacidade antes de gerar a arte.');
    if (!turnstileToken) return setError('Complete a validação anti-robô antes de gerar a arte.');
    setProcessing(true);
    setError('');
    try {
      const subject = await callPublicArtFunction(siteId, selectedTemplate.id, photo, turnstileToken);
      const result = await composePublicArt(selectedTemplate, subject);
      setPreviewUrl((url) => { if (url) URL.revokeObjectURL(url); return URL.createObjectURL(result); });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gerar sua arte.');
      setTurnstileToken(null);
    } finally {
      setProcessing(false);
    }
  };

  const download = () => {
    if (!previewUrl || !selectedTemplate) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = artFilename(selectedTemplate.title);
    link.click();
  };

  const share = async () => {
    if (!previewUrl || !selectedTemplate || !navigator.share) return;
    try {
      const blob = await fetch(previewUrl).then((response) => response.blob());
      const file = new File([blob], artFilename(selectedTemplate.title), { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ title: selectedTemplate.title, files: [file] });
      else await navigator.share({ title: selectedTemplate.title, url: previewUrl });
    } catch (err) {
      if ((err as DOMException | undefined)?.name !== 'AbortError') setError('Não foi possível abrir o compartilhamento neste dispositivo.');
    }
  };

  if (!siteId || (!isLoading && templates.length === 0)) return null;

  return (
    <section className="w-full py-10">
      <div className="mx-auto grid max-w-5xl gap-7 px-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,.72fr)]">
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-2 text-sm font-semibold text-accent"><Sparkles className="h-4 w-4" /> Arte para compartilhar</div>
          <h2 className="mt-3 font-display text-3xl font-bold text-ink">{block.title}</h2>
          {block.description && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">{block.description}</p>}

          {templates.length > 1 && (
            <label className="mt-6 block text-sm font-semibold text-ink">
              Escolha uma arte
              <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)} className="mt-2 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink">
                {templates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}
              </select>
            </label>
          )}

          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event: ChangeEvent<HTMLInputElement>) => choosePhoto(event.target.files?.[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} className="mt-6 flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-surface px-5 py-9 text-center transition-colors hover:border-accent hover:bg-accent/5">
            <ImagePlus className="h-6 w-6 text-accent" />
            <span className="text-sm font-semibold text-ink">{photo ? photo.name : 'Envie sua foto'}</span>
            <span className="text-xs text-muted">JPEG, PNG ou WebP, até 8 MB</span>
          </button>

          <label className="mt-5 flex gap-3 rounded-lg border border-border bg-surface p-3 text-xs leading-relaxed text-muted">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]" />
            <span>Autorizo o envio desta foto exclusivamente para gerar a arte solicitada. Ela será processada de forma temporária e não será salva na biblioteca do site.</span>
          </label>
          <div className="mt-4 flex justify-center"><Turnstile siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'} onSuccess={setTurnstileToken} options={{ theme: 'auto', language: 'pt-br', size: 'flexible' }} /></div>

          <button type="button" disabled={!photo || !selectedTemplate || !consent || !turnstileToken || processing} onClick={() => void generate()} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {processing ? 'Preparando sua arte...' : 'Gerar arte'}
          </button>
          {error && <p className="mt-3 text-center text-xs text-danger">{error}</p>}
        </div>

        <aside className="rounded-lg border border-border bg-surface p-4 shadow-sm">
          {previewUrl ? (
            <>
              <img src={previewUrl} alt="Prévia da arte gerada" className="mx-auto max-h-[560px] w-auto max-w-full rounded-none shadow-md" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button type="button" onClick={download} className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-white"><Download className="h-4 w-4" /> Baixar PNG</button>
                <button type="button" onClick={() => void share()} disabled={!navigator.share} className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-semibold text-ink disabled:opacity-50"><Share2 className="h-4 w-4" /> Compartilhar</button>
              </div>
            </>
          ) : selectedTemplate ? (
            <>
              <img src={selectedTemplate.base_image_url} alt={`Modelo ${selectedTemplate.title}`} className="mx-auto max-h-[560px] w-auto max-w-full rounded-none shadow-md" />
              <p className="mt-3 text-center text-xs text-muted">A prévia final aparecerá aqui antes do download.</p>
            </>
          ) : <div className="flex min-h-64 items-center justify-center text-sm text-muted">Carregando modelos...</div>}
          <p className="mt-4 flex gap-2 text-[11px] leading-relaxed text-muted"><ShieldCheck className="h-4 w-4 shrink-0 text-accent" /> Sua foto é usada apenas durante esta geração. Limite de três artes por dia.</p>
        </aside>
      </div>
    </section>
  );
}
